import { useActiveRoles, useActiveRolesLoadFailed, hasRole, isPublisher as checkIsPublisher, isPublisherRoleOnly as checkIsPublisherRoleOnly, } from "../utils/permissionsUtils";
import { RoleEnum } from "../types/RoleEnum";
import { ProductAccessibilityEnum, } from "../types/Api";
import { useGetCurrentUser } from "../utils/useCurrentUser";
import { hasAnyActiveOneOffRegistration } from "../utils/publisherRegistrationUtils";
/** ONIX Codelist 45 publishing-role value that marks a title publisher as a co-publisher. */
var CO_PUBLISHER_ROLE = "02";
export var usePermissions = function () {
    var _a;
    var activeRoles = useActiveRoles();
    var activeRolesError = useActiveRolesLoadFailed() && activeRoles === undefined;
    var currentUser = useGetCurrentUser().data;
    var checkRoles = function () {
        var roles = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            roles[_i] = arguments[_i];
        }
        return hasRole(activeRoles, RoleEnum.SUPERUSER) ||
            roles.some(function (role) { return hasRole(activeRoles, role); });
    };
    var userSourceId = (_a = currentUser === null || currentUser === void 0 ? void 0 : currentUser.activeOrganization) === null || _a === void 0 ? void 0 : _a.sourceId;
    /**
     * True when the logged-in publisher owns the title (its owner/guarantor).
     * Ownership — not merely being one of the title's publishers — is what gates
     * every edit/create/delete action for a publisher-only user.
     */
    var isTitleOwner = function (title) { var _a; return !!userSourceId && userSourceId === ((_a = title === null || title === void 0 ? void 0 : title.owner) === null || _a === void 0 ? void 0 : _a.sourceId); };
    /** True when the logged-in publisher owns the product's parent title. */
    var isProductTitleOwner = function (product) {
        return !!product && isTitleOwner(product.title);
    };
    /**
     * True when the logged-in publisher is listed as a co-publisher (ONIX role
     * "02") of the title — regardless of ownership.
     */
    var isTitleCoPublisher = function (title) {
        if (!userSourceId)
            return false;
        return ((title === null || title === void 0 ? void 0 : title.titlePublishers) || []).some(function (tp) {
            var _a, _b;
            return ((_a = tp.role) === null || _a === void 0 ? void 0 : _a.value) === CO_PUBLISHER_ROLE &&
                ((_b = tp.publisher) === null || _b === void 0 ? void 0 : _b.sourceId) === userSourceId;
        });
    };
    var canEditProductItem = function (product) {
        if (!product)
            return false;
        if (hasRole(activeRoles, RoleEnum.SUPERUSER))
            return true;
        if (hasRole(activeRoles, RoleEnum.AGENCY_WORKER))
            return true;
        if (checkIsPublisherRoleOnly(activeRoles)) {
            return isProductTitleOwner(product);
        }
        return false;
    };
    var canChangeProductAccessibility = function (product) {
        if (!product)
            return false;
        if (hasRole(activeRoles, RoleEnum.SUPERUSER))
            return true;
        if (hasRole(activeRoles, RoleEnum.AGENCY_WORKER))
            return true;
        if (checkIsPublisherRoleOnly(activeRoles)) {
            return isProductTitleOwner(product);
        }
        return false;
    };
    /**
     * Gates the product "Smazat" action. Agency worker / superuser may delete any
     * product (its numbers are then retired by the backend); a publisher-only
     * user only their own product that has no product number yet.
     */
    var canDeleteProductItem = function (product, hasNumber) {
        if (!product)
            return false;
        if (hasRole(activeRoles, RoleEnum.SUPERUSER))
            return true;
        if (hasRole(activeRoles, RoleEnum.AGENCY_WORKER))
            return true;
        if (checkIsPublisherRoleOnly(activeRoles)) {
            return !hasNumber && isProductTitleOwner(product);
        }
        return false;
    };
    var canEditTitleItem = function (title) {
        if (!title)
            return false;
        if (hasRole(activeRoles, RoleEnum.SUPERUSER))
            return true;
        if (hasRole(activeRoles, RoleEnum.AGENCY_WORKER))
            return true;
        if (checkIsPublisherRoleOnly(activeRoles)) {
            return isTitleOwner(title);
        }
        return false;
    };
    var canDeleteTitleItem = function (title) {
        return canEditTitleItem(title);
    };
    /**
     * Gates every "Vytvořit produkt" action for a title (products panel, title
     * detail and list action menus). Same as {@link canEditTitleItem}, except a
     * publisher-only user may not add products to a title whose owner has an
     * active ONE_OFF registration — its products are created automatically when
     * the one-off registration is approved. Agency worker / superuser unaffected.
     */
    var canCreateProductForTitle = function (title) {
        if (!canEditTitleItem(title))
            return false;
        if (checkIsPublisherRoleOnly(activeRoles)) {
            return !hasAnyActiveOneOffRegistration(title === null || title === void 0 ? void 0 : title.owner);
        }
        return true;
    };
    /**
     * True when the logged-in publisher owns the title behind a title-search
     * result row (the search DTO carries only `ownerSourceId`, not the full
     * `owner` object used by {@link isTitleOwner}).
     */
    var isTitleSearchItemOwner = function (item) {
        return !!userSourceId && userSourceId === (item === null || item === void 0 ? void 0 : item.ownerSourceId);
    };
    /**
     * Gates the row-level "Vytvořit produkt" quick action in the titles list.
     * Mirrors {@link canEditTitleItem}: agency/superuser can create a product
     * for any title, a publisher-only user only for a title they own — NOT for
     * a title where they are merely a co-publisher, and not for a title whose
     * owner has an active ONE_OFF registration (see {@link canCreateProductForTitle}).
     */
    var canCreateProductForTitleSearchItem = function (item) {
        if (!item)
            return false;
        if (hasRole(activeRoles, RoleEnum.SUPERUSER))
            return true;
        if (hasRole(activeRoles, RoleEnum.AGENCY_WORKER))
            return true;
        if (checkIsPublisherRoleOnly(activeRoles)) {
            return isTitleSearchItemOwner(item) && !item.ownerOneOff;
        }
        return false;
    };
    var isPublisher = checkIsPublisher(activeRoles);
    var isPublisherRoleOnly = checkIsPublisherRoleOnly(activeRoles);
    /**
     * True when the user may read only PUBLIC records — mirrors the backend
     * allow-list (UserRoleService.hasPublicOnlyView): every role except agency
     * worker, superuser and the publisher roles (e.g. Distributor, collections
     * or ONIX administrator) gets the public-only view. Unresolved or empty roles
     * are not public-only (the backend refuses such a user anyway).
     */
    var hasPublicOnlyView = (activeRoles !== null && activeRoles !== void 0 ? activeRoles : []).some(function (role) { return !!role; }) &&
        !hasRole(activeRoles !== null && activeRoles !== void 0 ? activeRoles : [], RoleEnum.AGENCY_WORKER) &&
        !hasRole(activeRoles !== null && activeRoles !== void 0 ? activeRoles : [], RoleEnum.SUPERUSER) &&
        !isPublisher;
    /**
     * Roles granted titles.preview.get / products.preview.get for every record
     * they can open (the backend still applies the guarantor scope of the detail).
     */
    var canPreviewAnyRecord = checkRoles(RoleEnum.AGENCY_WORKER, RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN);
    /**
     * True when the record preview is granted only for PUBLIC records: the
     * Distributor (RECEK-76074) previews a PUBLIC product and a title that has
     * one; the backend answers 403 for anything else. Other public-only roles
     * (e.g. collections administrator) hold no preview permission at all.
     */
    var previewNeedsPublicRecord = !canPreviewAnyRecord && hasRole(activeRoles !== null && activeRoles !== void 0 ? activeRoles : [], RoleEnum.DISTRIBUTOR);
    /** Gates the record preview ("Náhled ve formátu...") of a product. */
    var canPreviewProduct = function (product) {
        if (!product)
            return false;
        if (canPreviewAnyRecord)
            return true;
        return (previewNeedsPublicRecord &&
            product.accessibility === ProductAccessibilityEnum.PUBLIC);
    };
    /**
     * Gates the record preview ("Náhled ve formátu...") of a title. For the
     * Distributor the title's products decide (at least one PUBLIC product);
     * while they are unknown the preview is not offered.
     */
    var canPreviewTitle = function (title, products) {
        if (!title)
            return false;
        if (canPreviewAnyRecord)
            return true;
        return (previewNeedsPublicRecord &&
            (products !== null && products !== void 0 ? products : []).some(function (product) { return product.accessibility === ProductAccessibilityEnum.PUBLIC; }));
    };
    /**
     * True when the logged-in user is a publisher-only user (no agency/superuser
     * rights) who is NOT the title owner but IS listed as a co-publisher. Such a
     * user may only view the title (and request a co-edition ISBN); the detail is
     * read-only and shows a co-publisher notice.
     */
    var isTitleReadOnlyCoPublisher = function (title) {
        return isPublisherRoleOnly && !isTitleOwner(title) && isTitleCoPublisher(title);
    };
    return {
        canEditNaturalPerson: checkRoles(RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN),
        canDeleteNaturalPerson: checkRoles(RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN),
        canCreateNaturalPerson: checkRoles(RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN),
        canViewNaturalPerson: checkRoles(RoleEnum.AGENCY_WORKER, RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN, RoleEnum.DISTRIBUTOR),
        canEditLegalPerson: checkRoles(RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN),
        canDeleteLegalPerson: checkRoles(RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN),
        canCreateLegalPerson: checkRoles(RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN),
        canViewLegalPerson: checkRoles(RoleEnum.AGENCY_WORKER, RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN, RoleEnum.DISTRIBUTOR),
        canEditProduct: checkRoles(RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN, RoleEnum.AGENCY_WORKER),
        canCreateProduct: checkRoles(RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN, RoleEnum.AGENCY_WORKER),
        canViewProduct: checkRoles(RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN, RoleEnum.DISTRIBUTOR, RoleEnum.AGENCY_WORKER),
        /** Mirrors the role guard of the /titles/create route. */
        canCreateTitle: checkRoles(RoleEnum.AGENCY_WORKER, RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN),
        /*
         * Record preview ("Náhled ve formátu..."). Mirrors the backend grants of
         * titles.preview.get / products.preview.get: agency, publisher roles and
         * superuser for every record they can open, the Distributor only for a
         * PUBLIC product / a title with one (RECEK-76074).
         */
        canPreviewProduct: canPreviewProduct,
        canPreviewTitle: canPreviewTitle,
        previewNeedsPublicRecord: previewNeedsPublicRecord,
        /**
         * Gates blocking / unblocking a product number. Mirrors the backend grants
         * of product-numbers.item.block.put / unblock.put; the Distributor only browses.
         */
        canBlockProductNumber: checkRoles(RoleEnum.AGENCY_WORKER, RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN),
        /**
         * Gates loading and acting on co-edition number requests on a product
         * (the backend grants co-edition-number-requests.* to agency and publisher roles only).
         */
        canViewCoEditionRequests: checkRoles(RoleEnum.AGENCY_WORKER, RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN),
        /**
         * Gates links to the publisher detail. The Distributor browses no
         * publishers (FSD) and holds no permission for the publisher detail.
         */
        canViewPublisherDetail: checkRoles(RoleEnum.AGENCY_WORKER, RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN, RoleEnum.COLLECTIONS_ADMINISTRATOR),
        /**
         * Gates linking a local-only person to NK authorities — send, assign an NK
         * identifier, merge (DB-635). Agency worker / superuser only, mirroring the
         * backend (error -79 for anyone else).
         */
        canManagePersonAuthorityLink: checkRoles(RoleEnum.AGENCY_WORKER),
        canEditPublisherPrefix: checkRoles(RoleEnum.AGENCY_WORKER),
        canDeclareLastUsedNumber: checkRoles(RoleEnum.AGENCY_WORKER, RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN),
        canViewPublisherPrefix: checkRoles(RoleEnum.AGENCY_WORKER, RoleEnum.PUBLISHER_GUARANTOR, RoleEnum.PUBLISHER_ADMIN),
        canEditProductItem: canEditProductItem,
        canChangeProductAccessibility: canChangeProductAccessibility,
        canDeleteProductItem: canDeleteProductItem,
        canEditTitleItem: canEditTitleItem,
        canDeleteTitleItem: canDeleteTitleItem,
        canCreateProductForTitle: canCreateProductForTitle,
        canCreateProductForTitleSearchItem: canCreateProductForTitleSearchItem,
        isTitleOwner: isTitleOwner,
        isTitleSearchItemOwner: isTitleSearchItemOwner,
        isProductTitleOwner: isProductTitleOwner,
        isTitleCoPublisher: isTitleCoPublisher,
        isTitleReadOnlyCoPublisher: isTitleReadOnlyCoPublisher,
        isSuperUser: hasRole(activeRoles, RoleEnum.SUPERUSER),
        isPublisherGuarantor: hasRole(activeRoles, RoleEnum.PUBLISHER_GUARANTOR),
        isPublisherAdmin: hasRole(activeRoles, RoleEnum.PUBLISHER_ADMIN),
        isAgencyWorker: hasRole(activeRoles, RoleEnum.AGENCY_WORKER),
        isPublisher: isPublisher,
        isPublisherRoleOnly: isPublisherRoleOnly,
        hasPublicOnlyView: hasPublicOnlyView,
        /**
         * True when a search must not carry agency-only criteria: the public-only
         * view, or roles that could not be loaded (the backend would refuse them
         * with 403 -92 for a public-only user).
         */
        searchAsPublicOnly: hasPublicOnlyView || activeRolesError,
        /** the active roles; undefined until they are known (or after a failed load) */
        activeRoles: activeRoles,
        /** true when the active roles could not be loaded */
        activeRolesError: activeRolesError,
        hasRole: function (role) {
            return checkRoles(role);
        },
        hasAnyRole: function (roles) {
            return checkRoles.apply(void 0, roles);
        },
    };
};
